import React, { useEffect, useMemo, useState } from 'react';
import { MONO_PRIMARY, applyBrandLayout, applyBrandTheme, buildThemeVars, cleanHex, ensureLayoutFont, suggestFromImage } from '../../utils/brandTheme';
import { getActiveEntityBrand } from '../../utils/activeEntity';
import './BrandThemeEditor.css';

/**
 * Brand theme (Brand settings, and Admin → Brands). Choose RiskLo's colors or
 * the brand's: pick one main color (a swatch, the color picker, or from the
 * logo) and RiskLo derives the rest so text stays readable. Second color,
 * accent and background tint are optional fine-tuning. Saved with the page's
 * Save button.
 */

const SWATCHES = [
  ['#1d7554', 'Forest'], ['#16a34a', 'Green'], ['#0891b2', 'Teal'], ['#2563eb', 'Blue'], ['#4f46e5', 'Indigo'],
  ['#7c3aed', 'Violet'], ['#db2777', 'Pink'], ['#dc2626', 'Red'], ['#ea580c', 'Orange'], ['#ca8a04', 'Gold'], ['#334155', 'Slate'],
];

const FINE_TUNE = [
  { key: 'secondary', label: 'Second color', hint: 'Gradient ends and highlights. Blank = main color.' },
  { key: 'accent', label: 'Accent', hint: 'Small highlights like the PRO badge.' },
  { key: 'background', label: 'Background tint', hint: 'Tints the dark background. Blank = a calm version of the main color.' },
];

function ColorInput({ label, value, onChange, placeholder, hint, clearable }) {
  const valid = cleanHex(value);
  return (
    <label className="bte-field">
      <span className="bte-label">{label}</span>
      <span className="bte-input-row">
        <input type="color" value={valid || '#888888'} onChange={(e) => onChange(e.target.value)} aria-label={`${label} picker`} />
        <input type="text" value={value || ''} placeholder={placeholder} maxLength={7} onChange={(e) => onChange(e.target.value.trim())} spellCheck={false} />
        {clearable && value ? <button type="button" className="bte-clear" onClick={() => onChange('')} aria-label={`Clear ${label}`}>×</button> : null}
      </span>
      {hint || (value && !valid) ? <small className={`bte-hint${value && !valid ? ' is-error' : ''}`}>{value && !valid ? 'Use a color like #1d7554.' : hint}</small> : null}
    </label>
  );
}

/** A small mock of the app painted with the theme's variables. */
function Preview({ vars, light, mono, layout = 'classic', brandName, logoUrl }) {
  return (
    <div className={`bte-mock bte-layout-${layout}${light ? ' is-light' : ''}${mono ? ' is-mono' : ''}`} style={vars}>
      <div className="bte-mock-side">
        <div className="bte-mock-brand">
          {logoUrl ? <img src={logoUrl} alt="" /> : <span className="bte-mock-initial">{(brandName || 'B')[0]}</span>}
          <span>{brandName || 'Brand'}</span>
        </div>
        <div className="bte-mock-link is-active">Overview</div>
        <div className="bte-mock-link">Results</div>
        <div className="bte-mock-link">Blowout Risk <span className="bte-mock-pro">PRO</span></div>
      </div>
      <div className="bte-mock-main">
        <div className="bte-mock-card">
          <div className="bte-mock-kpi">+18.4%</div>
          <svg viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
            <polyline fill="none" strokeWidth="2.5" points="0,34 20,30 40,31 60,22 80,20 100,10 120,6" />
          </svg>
        </div>
        <div className="bte-mock-actions">
          <span className="bte-mock-btn">Run simulation</span>
          <span className="bte-mock-chip">Live</span>
        </div>
      </div>
    </div>
  );
}

export default function BrandThemeEditor({ theme, onChange, brandName, logoUrl, layout = 'classic', fonts = null }) {
  const t = theme || {};
  const [picking, setPicking] = useState(false);
  const [logoNote, setLogoNote] = useState(null); // { tone, text }
  const [logoColors, setLogoColors] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [mode, setMode] = useState(() => (t.primary && t.enabled !== false ? 'brand' : 'riskLo'));
  const isMono = t.style === 'mono';
  const hasPrimary = Boolean(cleanHex(t.primary));
  const usingBrand = mode === 'brand';
  const showMono = usingBrand && isMono;
  const vars = useMemo(() => {
    if (!usingBrand || !hasPrimary) return {};
    return buildThemeVars({ ...(theme || {}), enabled: true });
  }, [theme, usingBrand, hasPrimary]);

  const set = (key, value) => onChange({ ...t, style: null, enabled: true, [key]: value });
  const choose = (hex) => { setMode('brand'); if (logoNote?.tone === 'warn') setLogoNote(null); onChange({ ...t, style: null, enabled: true, primary: hex }); };
  const chooseMono = () => { setMode('brand'); if (logoNote?.tone === 'warn') setLogoNote(null); onChange({ enabled: true, style: 'mono', primary: MONO_PRIMARY }); };

  const switchMode = (next) => {
    setMode(next);
    if (next === 'riskLo') onChange(t.primary ? { ...t, enabled: false } : null);
    else if (t.primary) onChange({ ...t, enabled: true });
  };

  const fromLogo = async () => {
    if (!logoUrl) { setLogoNote({ tone: 'warn', text: 'Upload a logo first.' }); return; }
    setPicking(true);
    setLogoNote(null);
    const r = await suggestFromImage(logoUrl);
    setPicking(false);
    if (r.primary) {
      setLogoColors(r.colors || [r.primary]);
      setMode('brand');
      onChange({ ...t, style: null, enabled: true, primary: r.primary, accent: r.accent || t.accent || '' });
      setLogoNote({ tone: 'ok', text: `Took ${r.primary} from your logo${r.colors?.length > 1 ? '. Other logo colors are in the swatches below.' : '.'}` });
    } else if (r.reason === 'monochrome') {
      // A black, white or gray logo gets the black & white look, like any other logo color.
      chooseMono();
      setLogoNote({ tone: 'ok', text: 'Your logo is black and white, so we matched it: near-black with white in dark mode, white with black in light mode.' });
    } else {
      setLogoNote({ tone: 'warn', text: 'Couldn’t read the logo image. Pick a main color below, or upload the logo again.' });
    }
  };

  useEffect(() => { ensureLayoutFont(layout); }, [layout]);
  // While previewing, a layout change shows on the whole app straight away.
  useEffect(() => { if (previewing) applyBrandLayout({ preset: layout, fonts }); }, [layout, fonts, previewing]);

  // Paint the whole app with this theme and layout until "Stop preview" (nothing is saved).
  const togglePreview = () => {
    if (previewing) {
      const saved = getActiveEntityBrand()?.branding;
      applyBrandTheme(saved?.theme || null);
      applyBrandLayout(saved?.layout || null);
      setPreviewing(false);
    } else {
      if (usingBrand && hasPrimary) applyBrandTheme({ ...t, enabled: true });
      applyBrandLayout({ preset: layout, fonts });
      setPreviewing(true);
    }
  };

  const swatches = [...logoColors.map((c) => [c, 'From your logo']), ...SWATCHES.filter(([c]) => !logoColors.includes(c))];

  return (
    <div className="bte">
      <div className="bte-modes" role="radiogroup" aria-label="App colors">
        <button type="button" role="radio" aria-checked={!usingBrand} className={`bte-mode${!usingBrand ? ' is-on' : ''}`} onClick={() => switchMode('riskLo')}>
          <span className="bte-mode-dot bte-mode-dot-default" />
          <span><strong>RiskLo colors</strong><small>The standard purple look</small></span>
        </button>
        <button type="button" role="radio" aria-checked={usingBrand} className={`bte-mode${usingBrand ? ' is-on' : ''}`} onClick={() => switchMode('brand')}>
          <span className={`bte-mode-dot${isMono && hasPrimary ? ' bte-mode-dot-mono' : ''}`} style={hasPrimary && !isMono ? { background: t.primary } : undefined} />
          <span><strong>Your brand colors</strong><small>Pick a color, black &amp; white, or take it from your logo</small></span>
        </button>
      </div>

      <div className="bte-grid">
        <div className={`bte-fields${usingBrand ? '' : ' is-muted'}`}>
          {usingBrand ? (
            <>
              <div className="bte-step">
                <span className="bte-label">Main color</span>
                <div className="bte-swatches">
                  <button
                    type="button"
                    className={`bte-swatch bte-swatch-mono${showMono ? ' is-on' : ''}`}
                    onClick={chooseMono}
                    aria-label="Black and white"
                    title="Black & white · near-black with white, flips in light mode"
                  />
                  {swatches.map(([hex, name]) => (
                    <button
                      key={hex}
                      type="button"
                      className={`bte-swatch${!isMono && cleanHex(t.primary) === hex ? ' is-on' : ''}${name === 'From your logo' ? ' is-logo' : ''}`}
                      style={{ background: hex }}
                      onClick={() => choose(hex)}
                      aria-label={`${name} ${hex}`}
                      title={`${name} · ${hex}`}
                    />
                  ))}
                </div>
                <div className="bte-row">
                  <ColorInput label="Or any color" value={isMono ? '' : (t.primary || '')} placeholder={isMono ? 'Black & white' : '#1d7554'} onChange={(v) => set('primary', v)} />
                  <button type="button" className="bte-btn" onClick={fromLogo} disabled={picking}>
                    {picking ? 'Reading logo…' : 'Pick from logo'}
                  </button>
                </div>
                {logoNote ? <p className={`bte-callout is-${logoNote.tone}`} role="status">{logoNote.text}</p> : null}
              </div>

              {!isMono ? (
                <details className="bte-more">
                  <summary>Fine-tune colors <small>(optional)</small></summary>
                  <div className="bte-more-body">
                    {FINE_TUNE.map((f) => (
                      <ColorInput key={f.key} label={f.label} hint={f.hint} value={t[f.key] || ''} placeholder="auto" clearable onChange={(v) => set(f.key, v)} />
                    ))}
                  </div>
                </details>
              ) : null}
            </>
          ) : (
            <p className="bte-note">Members see RiskLo’s standard colors. Choose <strong>Your brand colors</strong> to pick one color (or black &amp; white, or take it from your logo); RiskLo works out the rest and keeps text readable.</p>
          )}
        </div>

        <div className="bte-previews">
          <div className="bte-preview-head">
            <span className="bte-label">Preview{usingBrand && !hasPrimary ? ' (pick a color)' : ''}</span>
            <button type="button" className="bte-btn bte-btn-ghost" onClick={togglePreview}>{previewing ? 'Stop preview' : 'Try it on the whole app'}</button>
          </div>
          <Preview vars={vars} mono={showMono} layout={layout} brandName={brandName} logoUrl={logoUrl} />
          <Preview vars={vars} light mono={showMono} layout={layout} brandName={brandName} logoUrl={logoUrl} />
          <small className="bte-hint">Dark and light mode, in the {layout === 'clean' ? 'Clean' : layout === 'sharp' ? 'Sharp' : 'Classic'} layout. Changes apply when you save.</small>
        </div>
      </div>
    </div>
  );
}
